import CloseIcon from "@mui/icons-material/Close";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import {
  useCallback,
  useContext,
  useEffect,
  useState,
  type CSSProperties,
  type FormEvent,
  type KeyboardEvent,
} from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { ReactComponent as SelectChatIcon } from "../../assets/Chat/select-chat.svg";
import SearchIcon from "../../assets/Chat/magnifier.svg";
import { ReactComponent as ChevronIcon } from "../../assets/Chat/chevron-up.svg";
import CircleAvatar from "../../components/CircleAvatar/CircleAvatar";
import ConfirmationModal from "../../components/ConfirmationModal/ConfirmationModal";
import Input from "../../components/Input/Input";
import Loader from "../../components/Loader/Loader";
import ModalWindow from "../../components/ModalWindow/ModalWindow";
import TripleDotPoperMenu from "../../components/TripleDotPoperMenu/TripleDotPoperMenu";
import {
  TextChatComposer,
  TextChatMessage as SharedTextChatMessage,
  TextChatMessageList,
  TextChatVoiceControl,
  TEXT_CHAT_AVAILABILITY_BY_STATUS,
  TEXT_CHAT_MODE_CONTRACTS,
} from "../../components/TextChat";
import {
  TextChatAvailability,
  TextChatMessage as TextChatMessageData,
} from "../../components/TextChat/TextChat.types";
import { ContextProvider } from "../../contextProvider";
import { dialogChatSocketAdapter } from "../../features/dialogChat/dialogChatSocketAdapter";
import useInfiniteScroll from "../../hooks/InfinityScroll";
import strings from "../../strings";
import { DialogChat } from "../../types/DialogChat";
import { getTranslation } from "../../utils/getTranslation";
import ChatAvatars from "./ChatAvatars/ChatAvatars";
import ChatContactElement from "./ChatContactElement/ChatContactElement";
import ChatHeaderMenu from "./ChatHeaderMenu/ChatHeaderMenu";
import s from "./GlobalChat.module.css";
import {
  focusGlobalChatMessageTarget,
  prefersReducedMotion,
} from "./model/globalChatA11y";
import { GlobalChatMessage } from "./model/globalChatCache";
import {
  createReplyPreviewFromMessage,
  getReplyPreviewSummary,
} from "./model/globalChatComposition";
import {
  getGlobalChatConversationActionAvailability,
  getGlobalChatConversationKind,
  includesUserId,
  isGlobalChatConversationPinned,
} from "./model/globalChatConversationActions";
import { useGlobalChatAttachmentResources } from "./model/useGlobalChatAttachmentResources";
import { useGlobalChatBookingResources } from "./model/useGlobalChatBookingResources";
import { useGlobalChatController } from "./model/useGlobalChatController";
import MassMailing from "./MassMailing/MassMailing";

const ADMIN_ID = process.env.REACT_APP_ADMIN_ID;
const globalTextChatContract = TEXT_CHAT_MODE_CONTRACTS.global;

const GlobalChat = () => {
  const token = localStorage.getItem("token");
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [isMessageSearchOpen, setIsMessageSearchOpen] = useState(false);
  const [isPinnedMessagesOpen, setIsPinnedMessagesOpen] = useState(false);
  const [isUnpinAllModalOpen, setIsUnpinAllModalOpen] = useState(false);
  const [isUnpinningAllPinnedMessages, setIsUnpinningAllPinnedMessages] =
    useState(false);
  const [isVoiceDraftActive, setIsVoiceDraftActive] = useState(false);
  const [massMailModal, setMassMailModal] = useState(false);
  const [localUnreadMessagesCount, setLocalUnreadMessagesCount] = useState(0);
  const { userData, userIdForCreate, setUserIdForCreate } =
    useContext(ContextProvider);
  const handleDirectUserIdHandled = useCallback(() => {
    setUserIdForCreate(null);
  }, [setUserIdForCreate]);

  const controller = useGlobalChatController({
    token,
    currentUser: userData,
    routeState: location.state,
    searchParams,
    directUserId: userIdForCreate,
    onDirectUserIdHandled: handleDirectUserIdHandled,
    socketAdapter: dialogChatSocketAdapter,
    unreadMessagesCount: localUnreadMessagesCount,
    onUnreadMessagesCountChange: setLocalUnreadMessagesCount,
    capabilities: {
      reactions: globalTextChatContract.capabilities.reactions,
    },
  });

  const { scrollContainerRef } = useInfiniteScroll({
    fetchData: controller.actions.loadConversations,
    hasMore: controller.conversationsHasMore,
    isLoading: controller.conversationsLoading,
    initialPage: 0,
  });

  const selectedChat = controller.selectedChat;
  const selectedMessageData = controller.selectedMessages;
  const pinnedMessagesCount = controller.selectedPinnedMessages.length;
  const latestPinnedMessage = controller.selectedPinnedMessages[0];
  const lastMessage = controller.lastMessageByChatId;
  const updateMessage = controller.actions.updateMessage;
  const handleBookingMessageRefresh = useCallback(
    (message: GlobalChatMessage) => {
      updateMessage(message.messageId, {
        realTimeBooking: message.realTimeBooking,
      });
    },
    [updateMessage],
  );
  const attachmentResources = useGlobalChatAttachmentResources(
    token,
    selectedMessageData,
  );
  const bookingResources = useGlobalChatBookingResources({
    token,
    currentUser: userData,
    messages: selectedMessageData,
    onBookingMessageRefresh: handleBookingMessageRefresh,
  });

  const isSystemMessage = (message: GlobalChatMessage) =>
    message.user?.toString() === "System";

  const getMessageUser = (message: GlobalChatMessage) =>
    message.user && message.user !== "System" ? message.user : undefined;

  const getMessagePreviewText = (message: GlobalChatMessage) =>
    message.text ||
    (message.files.length > 0
      ? strings.globalChatAttachmentPreview
      : message.voice
        ? strings.globalChatVoiceMessagePreview
        : strings.globalChatMessagePreview);

  const setDeleteModalVisible = useCallback(
    (value: boolean | ((previous: boolean) => boolean)) => {
      const nextValue =
        typeof value === "function"
          ? value(Boolean(controller.pendingDeleteMessage))
          : value;

      if (!nextValue) {
        controller.actions.cancelDeleteMessage();
      }
    },
    [controller.actions, controller.pendingDeleteMessage],
  );

  const setUnpinAllModalVisible = useCallback(
    (value: boolean | ((previous: boolean) => boolean)) => {
      const nextValue =
        typeof value === "function" ? value(isUnpinAllModalOpen) : value;

      setIsUnpinAllModalOpen(nextValue);
    },
    [isUnpinAllModalOpen],
  );

  const handleUnpinAllPinnedMessages = useCallback(async () => {
    setIsUnpinningAllPinnedMessages(true);

    try {
      const didUnpinAll = await controller.actions.unpinAllPinnedMessages();

      if (didUnpinAll) {
        setIsPinnedMessagesOpen(false);
        setIsUnpinAllModalOpen(false);
      }
    } finally {
      setIsUnpinningAllPinnedMessages(false);
    }
  }, [controller.actions]);

  const getMessageFocusId = (messageId: string) =>
    `global-chat-message-${messageId}`;
  const clearFocusedMessageTarget =
    controller.actions.clearFocusedMessageTarget;

  useEffect(() => {
    if (!controller.focusTargetMessageId) {
      return;
    }

    const target = document.getElementById(
      getMessageFocusId(controller.focusTargetMessageId),
    );
    if (!target) {
      return;
    }

    focusGlobalChatMessageTarget(target, {
      reducedMotion: prefersReducedMotion(),
    });
    clearFocusedMessageTarget();
  }, [
    clearFocusedMessageTarget,
    controller.focusTargetMessageId,
    selectedMessageData,
  ]);

  useEffect(() => {
    if (pinnedMessagesCount <= 1) {
      setIsPinnedMessagesOpen(false);
    }

    if (pinnedMessagesCount === 0) {
      setIsUnpinAllModalOpen(false);
    }
  }, [pinnedMessagesCount]);

  useEffect(() => {
    setIsPinnedMessagesOpen(false);
    setIsUnpinAllModalOpen(false);
  }, [selectedChat?._id]);

  const handleMessageSearchSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    controller.actions.searchMessages();
  };

  const normalizedMessages = selectedMessageData.map<TextChatMessageData>(
    (message) => {
      const messageUser = getMessageUser(message);

      return {
        id: message.messageId,
        type: message.voice
          ? "voice"
          : message.realTimeBooking
            ? "booking"
            : isSystemMessage(message)
              ? "system"
              : "text",
        senderKind: isSystemMessage(message)
          ? "system"
          : messageUser?._id === userData?._id
            ? "currentUser"
            : "otherUser",
        sender: messageUser
          ? {
              id: messageUser._id,
              name: messageUser.name,
              surname: messageUser.surname,
              avatar: messageUser.avatarS3 || messageUser.avatar,
            }
          : undefined,
        text: message.text,
        date: new Date(message.date),
        attachments: message.files.map((file, index) => ({
          id:
            typeof file === "string"
              ? file
              : file?._id || `${message.messageId}-attachment-${index}`,
          name:
            typeof file === "string"
              ? file
              : file?.filename || file?.name || `attachment-${index + 1}`,
          type: typeof file === "string" ? "file" : file?.type || "file",
          size: typeof file === "string" ? 0 : Number(file?.size || 0),
          original: typeof file === "string" ? undefined : file,
        })),
        reactions: message.reactions,
        readBy: message.readBy || [],
        deliveryState: message.deliveryState,
        voice: message.voice,
        replyToMessage: message.replyToMessage,
        realTimeBooking: message.realTimeBooking,
        editedAt: message.editedAt,
      };
    },
  );

  const chatAvailability: TextChatAvailability =
    controller.isSelectedChatBlocked
      ? {
          ...TEXT_CHAT_AVAILABILITY_BY_STATUS[
            controller.selectedChatBlockedBy.includes(userData?._id ?? "")
              ? "blockedByMe"
              : "blockedByAnotherUser"
          ],
          messageKey: undefined,
        }
      : controller.isSelectedChatExpired
        ? {
            ...TEXT_CHAT_AVAILABILITY_BY_STATUS.expired,
            messageKey: undefined,
          }
        : TEXT_CHAT_AVAILABILITY_BY_STATUS.available;

  const getChatContactFullname = (chat: DialogChat) =>
    controller.helpers.getConversationFullname(chat);

  const getChatContactTitle = (chat: DialogChat) =>
    controller.helpers.getConversationTitle(chat);

  const getChatContactLabel = (
    chat: DialogChat,
    name: string,
    isAdminChat: boolean,
    isSelectedChat: boolean,
  ) => {
    const labelParts = [
      strings.formatString("globalChatOpenConversationWith", { name }),
    ];

    if (isAdminChat) {
      labelParts.push(strings.globalChatSupportConversation);
    }

    if (chat.unreadMessages > 0) {
      labelParts.push(
        strings.formatString("globalChatUnreadMessages", {
          count: chat.unreadMessages,
        }),
      );
    }

    if (isSelectedChat) {
      labelParts.push(strings.globalChatSelectedConversation);
    }

    return labelParts.join(". ");
  };

  const getConversationMenuItems = (chat: DialogChat) => {
    const actionAvailability = getGlobalChatConversationActionAvailability(
      chat,
      {
        currentUserId: userData?._id,
        adminUserId: ADMIN_ID,
      },
    );
    const isMuted = includesUserId(chat.mutedBy, userData?._id);
    const isBlocked = includesUserId(chat.blockedBy, userData?._id);

    return [
      actionAvailability.pin && {
        title: strings.pinChat,
        action: () => controller.actions.pinConversation(chat),
      },
      actionAvailability.unpin && {
        title: strings.unpinChat,
        action: () => controller.actions.unpinConversation(chat),
      },
      (actionAvailability.mute || actionAvailability.unmute) && {
        title: isMuted
          ? strings.enableNotifications
          : strings.disableNotifications,
        action: () => controller.actions.toggleMuteConversation(chat),
      },
      (actionAvailability.block || actionAvailability.unblock) && {
        title: isBlocked ? strings.unblockChat : strings.blockChat,
        action: () => controller.actions.toggleBlockConversation(chat),
      },
      actionAvailability.leave && {
        title: strings.leaveChat,
        action: () => controller.actions.leaveConversation(chat),
        styles: { color: "var(--main-red)" },
      },
    ].filter(Boolean) as {
      title: string;
      action: () => void;
      disabled?: boolean;
      styles?: CSSProperties;
    }[];
  };

  const selectChatFromKeyboard = (
    event: KeyboardEvent<HTMLDivElement>,
    chat: DialogChat,
  ) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      controller.actions.selectChat(chat);
    }
  };

  const renderConversation = (item: DialogChat) => {
    const fullname = getChatContactFullname(item);
    const title = getChatContactTitle(item);
    const contactName = title || fullname || strings.chatTitle;
    const isAdminChat =
      getGlobalChatConversationKind(item, ADMIN_ID) === "support";
    const isSelectedChat = selectedChat?._id === item._id;
    const localLastMessage = lastMessage[item._id];
    const currentUserId = userData?._id;

    return (
      <div
        key={item._id}
        role="button"
        tabIndex={0}
        className={s.contactButton}
        aria-label={getChatContactLabel(
          item,
          contactName,
          isAdminChat,
          isSelectedChat,
        )}
        aria-current={isSelectedChat ? "true" : undefined}
        onClick={() => controller.actions.selectChat(item)}
        onKeyDown={(event) => selectChatFromKeyboard(event, item)}
      >
        <ChatContactElement
          isAdminChat={isAdminChat}
          membersIds={item.members.map((item) => item._id)}
          dateLastMessage={
            localLastMessage?.date
              ? localLastMessage.date
              : item.lastMessage
                ? item.lastMessage.date
                : null
          }
          fullname={fullname}
          title={title}
          lastMessage={
            localLastMessage
              ? localLastMessage.message
              : item.lastMessage &&
                  item.lastMessage.labels &&
                  item.lastMessage.labels.find(
                    (item) => item.language === userData?.selectedLanguage,
                  )
                ? item.lastMessage.labels.find(
                    (item) => item.language === userData?.selectedLanguage,
                  )?.text!
                : item.lastMessage && item.lastMessage.message
                  ? item.lastMessage.message
                  : null
          }
          unreadMessages={item.unreadMessages}
          isSelectedChat={isSelectedChat}
          isPinned={isGlobalChatConversationPinned(item, currentUserId)}
          isMuted={includesUserId(item.mutedBy, currentUserId)}
          isBlocked={includesUserId(item.blockedBy, currentUserId)}
          menuItemData={getConversationMenuItems(item)}
        />
      </div>
    );
  };

  const conversationSections = [
    {
      key: "pinned",
      title: strings.pinnedChats,
      conversations: controller.groupedConversations.pinned,
    },
    {
      key: "unpinned",
      title: strings.recentChats,
      conversations: controller.groupedConversations.unpinned,
    },
  ].filter((section) => section.conversations.length > 0);

  return (
    <div className={s.container}>
      <div
        className={
          selectedChat
            ? `${s.contactsBlock} ${s.mobileContactsBlockHidden}`
            : `${s.contactsBlock} ${s.mobileContactsBlockVisible}`
        }
      >
        <div className={s.searchBlock}>
          <Input
            onChangeInput={controller.actions.setConversationsSearch}
            inputValue={controller.conversationsSearch}
            placeholder={strings.searcgContactPlaceholder}
            isVisible
            required
          />
          <img src={SearchIcon} alt="" className={s.searchIcon} />
          <TripleDotPoperMenu
            menuItemDta={[
              {
                title: "Refresh chats",
                action: () => {
                  void controller.actions.loadConversations(0);
                },
              },
              {
                title: "Mass mailing",
                action: () => setMassMailModal(true),
              },
            ]}
          />
        </div>
        <div className={s.contactsListBlock} ref={scrollContainerRef}>
          {controller.conversations.length ? (
            conversationSections.map((section) => (
              <section className={s.conversationSection} key={section.key}>
                <h3 className={s.conversationSectionTitle}>{section.title}</h3>
                {section.conversations.map(renderConversation)}
              </section>
            ))
          ) : !controller.conversationsLoaded ? (
            <div
              className={s.initialChatsLoader}
              role="status"
              aria-label={strings.loading}
            >
              <Loader size={40} />
            </div>
          ) : (
            <div className={s.noChats}>
              <h2>{strings.noChats}</h2>
            </div>
          )}
        </div>
      </div>
      {selectedChat ? (
        <div
          className={
            selectedChat
              ? `${s.messsagesBlock} ${s.mobileMessagesBlockVisible}`
              : `${s.messsagesBlock} ${s.mobileMessagesBlockHidden}`
          }
        >
          <div className={s.chatHeaderBlock}>
            <div className={s.leftHeaderChatBlock}>
              <button
                type="button"
                className={s.chevronBackButton}
                aria-label={strings.globalChatBackToContacts}
                onClick={() => controller.actions.selectChat(null)}
              >
                <ChevronIcon className={s.chevronBack} />
              </button>
              <div style={{ marginRight: "10px" }}>
                <ChatAvatars
                  userIds={selectedChat.members.map((item) => item._id)}
                  avatarsSize={{
                    oneAvtaar: window.innerWidth < 768 ? "50px" : "60px",
                    multipleAvatars: "30px",
                  }}
                />
              </div>
              {selectedChat.members.length === 1 ? (
                <span className={s.chatHeaderNameTitle}>
                  {controller.helpers.getConversationFullname(selectedChat)}
                </span>
              ) : selectedChat.title ? (
                <span>
                  {controller.helpers.getConversationTitle(selectedChat)}
                </span>
              ) : (
                <span>{strings.groupChat}</span>
              )}
            </div>
            <div className={s.chatHeaderActions}>
              <button
                type="button"
                className={s.headerIconButton}
                aria-label={strings.globalChatOpenMessageSearch}
                aria-expanded={isMessageSearchOpen}
                onClick={() => setIsMessageSearchOpen(true)}
              >
                <SearchOutlinedIcon fontSize="small" />
              </button>
              <ChatHeaderMenu
                chat={selectedChat}
                onSelectChat={controller.actions.selectChat}
                onUpsertChat={controller.actions.upsertConversation}
                onPinChat={controller.actions.pinConversation}
                onUnpinChat={controller.actions.unpinConversation}
                onToggleMuteChat={controller.actions.toggleMuteConversation}
                onToggleBlockChat={controller.actions.toggleBlockConversation}
                onLeaveChat={controller.actions.leaveConversation}
              />
            </div>
          </div>
          {isMessageSearchOpen && (
            <section
              className={s.messageSearchBlock}
              aria-label={strings.globalChatMessageSearch}
            >
              <form
                className={s.messageSearchForm}
                onSubmit={handleMessageSearchSubmit}
              >
                <input
                  className={s.messageSearchInput}
                  value={controller.messageSearchQuery}
                  placeholder={strings.globalChatSearchMessages}
                  aria-label={strings.globalChatSearchMessagesInConversation}
                  onChange={(event) =>
                    controller.actions.setMessageSearchQuery(event.target.value)
                  }
                />
                <button
                  type="submit"
                  className={s.messageSearchButton}
                  aria-label={strings.globalChatSearchMessages}
                  disabled={
                    controller.isMessageSearchLoading ||
                    !controller.messageSearchQuery.trim()
                  }
                >
                  {controller.isMessageSearchLoading
                    ? strings.globalChatSearchingMessages
                    : strings.globalChatSearchMessages}
                </button>
                {(controller.messageSearchQuery ||
                  controller.messageSearchResults.length > 0) && (
                  <button
                    type="button"
                    className={s.messageSearchClearButton}
                    aria-label={strings.globalChatClearMessageSearch}
                    onClick={controller.actions.clearMessageSearch}
                  >
                    {strings.globalChatClear}
                  </button>
                )}
                <button
                  type="button"
                  className={s.messageSearchClearButton}
                  aria-label={strings.globalChatCloseMessageSearch}
                  onClick={() => setIsMessageSearchOpen(false)}
                >
                  <CloseIcon fontSize="small" />
                </button>
              </form>
              {(controller.messageSearchError ||
                controller.messageSearchResults.length > 0 ||
                controller.isJumpingToSearchResult) && (
                <div
                  className={s.messageSearchResults}
                  role="region"
                  aria-label={strings.globalChatMessageSearchResults}
                >
                  {controller.messageSearchError && (
                    <div className={s.messageSearchStatus} role="status">
                      {controller.messageSearchError}
                    </div>
                  )}
                  {controller.isJumpingToSearchResult && (
                    <div className={s.messageSearchStatus} role="status">
                      {strings.globalChatOpeningSearchResult}
                    </div>
                  )}
                  {controller.messageSearchResults.map((message) => (
                    <button
                      type="button"
                      key={message.messageId}
                      className={s.messageSearchResult}
                      aria-label={
                        strings.formatString("globalChatOpenSearchResult", {
                          preview: getMessagePreviewText(message),
                        }) as string
                      }
                      onClick={() =>
                        controller.actions.openSearchResult(message)
                      }
                    >
                      <span className={s.messageSearchResultText}>
                        {getMessagePreviewText(message)}
                      </span>
                      <span className={s.messageSearchResultDate}>
                        {new Date(message.date).toLocaleString()}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </section>
          )}
          {(pinnedMessagesCount > 0 ||
            controller.pinnedMessageError ||
            controller.isJumpingToPinnedMessage) && (
            <section
              className={s.pinnedMessagesBlock}
              aria-label={strings.globalChatPinnedMessages}
            >
              {latestPinnedMessage && (
                <div className={s.pinnedMessageSummary}>
                  <button
                    type="button"
                    className={s.pinnedMessageSummaryButton}
                    aria-label={
                      strings.formatString("globalChatOpenPinnedMessage", {
                        preview: latestPinnedMessage.previewText,
                      }) as string
                    }
                    onClick={() =>
                      controller.actions.openPinnedMessage(
                        latestPinnedMessage.messageId,
                      )
                    }
                  >
                    <PushPinOutlinedIcon
                      className={s.pinnedMessageIcon}
                      fontSize="small"
                    />
                    <span className={s.pinnedMessageSummaryTextBlock}>
                      <span className={s.pinnedMessagesTitle}>
                        {pinnedMessagesCount > 1
                          ? (strings.formatString(
                              "globalChatPinnedMessagesCount",
                              {
                                count: pinnedMessagesCount,
                              },
                            ) as string)
                          : strings.globalChatPinnedMessages}
                      </span>
                      <span className={s.pinnedMessageText}>
                        {latestPinnedMessage.previewText}
                      </span>
                    </span>
                    <span className={s.pinnedMessageDate}>
                      {new Date(latestPinnedMessage.pinnedAt).toLocaleString()}
                    </span>
                  </button>
                  {(pinnedMessagesCount > 1 || chatAvailability.canWrite) && (
                    <div className={s.pinnedMessageSummaryActions}>
                      {pinnedMessagesCount > 1 && (
                        <button
                          type="button"
                          className={s.pinnedMessagesViewAllButton}
                          aria-label={strings.globalChatViewAllPinnedMessages}
                          aria-expanded={isPinnedMessagesOpen}
                          onClick={() =>
                            setIsPinnedMessagesOpen((previous) => !previous)
                          }
                        >
                          <FormatListBulletedIcon fontSize="small" />
                          <span>{strings.globalChatViewAllPinnedMessages}</span>
                        </button>
                      )}
                      {pinnedMessagesCount === 1 &&
                        chatAvailability.canWrite && (
                          <button
                            type="button"
                            className={s.pinnedMessageUnpinButton}
                            aria-label={strings.globalChatActionUnpin}
                            title={strings.globalChatActionUnpin}
                            onClick={() =>
                              controller.actions.unpinPinnedMessage(
                                latestPinnedMessage.messageId,
                              )
                            }
                          >
                            <CloseIcon fontSize="small" />
                          </button>
                        )}
                    </div>
                  )}
                </div>
              )}
              {controller.pinnedMessageError && (
                <div className={s.messageSearchStatus} role="status">
                  {controller.pinnedMessageError}
                </div>
              )}
              {controller.isJumpingToPinnedMessage && (
                <div className={s.messageSearchStatus} role="status">
                  {strings.globalChatOpeningPinnedMessage}
                </div>
              )}
              {isPinnedMessagesOpen && pinnedMessagesCount > 1 && (
                <div
                  className={s.pinnedMessagesPanel}
                  role="region"
                  aria-label={strings.globalChatPinnedMessagesList}
                >
                  <div className={s.pinnedMessagesPanelHeader}>
                    <h3 className={s.pinnedMessagesPanelTitle}>
                      {strings.globalChatPinnedMessages}
                    </h3>
                    <div className={s.pinnedMessagesPanelActions}>
                      {chatAvailability.canWrite && (
                        <button
                          type="button"
                          className={s.pinnedMessagesUnpinAllButton}
                          onClick={() => setIsUnpinAllModalOpen(true)}
                        >
                          {strings.globalChatUnpinAllPinnedMessages}
                        </button>
                      )}
                      <button
                        type="button"
                        className={s.messageSearchClearButton}
                        aria-label={strings.globalChatClosePinnedMessages}
                        onClick={() => setIsPinnedMessagesOpen(false)}
                      >
                        <CloseIcon fontSize="small" />
                      </button>
                    </div>
                  </div>
                  <ul className={s.pinnedMessagesList}>
                    {controller.selectedPinnedMessages.map((pin) => (
                      <li
                        key={`${pin.messageId}-${pin.pinnedAt}`}
                        className={s.pinnedMessageItem}
                      >
                        <button
                          type="button"
                          className={s.pinnedMessageButton}
                          aria-label={
                            strings.formatString(
                              "globalChatOpenPinnedMessage",
                              {
                                preview: pin.previewText,
                              },
                            ) as string
                          }
                          onClick={() =>
                            controller.actions.openPinnedMessage(pin.messageId)
                          }
                        >
                          <span className={s.pinnedMessageText}>
                            {pin.previewText}
                          </span>
                          <span className={s.pinnedMessageDate}>
                            {new Date(pin.pinnedAt).toLocaleString()}
                          </span>
                        </button>
                        {chatAvailability.canWrite && (
                          <button
                            type="button"
                            className={s.pinnedMessageUnpinButton}
                            aria-label={strings.globalChatActionUnpin}
                            title={strings.globalChatActionUnpin}
                            onClick={() =>
                              controller.actions.unpinPinnedMessage(
                                pin.messageId,
                              )
                            }
                          >
                            <CloseIcon fontSize="small" />
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </section>
          )}
          <TextChatMessageList
            conversationKey={selectedChat._id}
            messages={normalizedMessages}
            className={s.chatHistoryBlock}
            canLoadOlder={controller.selectedMessagesHasMore}
            canLoadNewer={controller.selectedMessagesHasNewer}
            isLoadingOlder={controller.selectedMessagesLoading}
            isLoadingNewer={controller.selectedMessagesLoading}
            onLoadOlder={controller.actions.loadOlderMessages}
            onLoadNewer={controller.actions.loadNewerMessages}
            onScrollToLatest={controller.actions.loadLatestMessages}
            locale={userData?.selectedLanguage}
            renderMessage={(item) => {
              const message = controller.selectedMessagesById.get(
                item.message.id,
              );
              const messageUser = message ? getMessageUser(message) : undefined;

              if (!message) {
                return null;
              }

              const messageActions =
                controller.actions.getMessageActions(message);
              const attachmentSnapshot =
                attachmentResources.getAttachmentSnapshot(message);
              const resolvedFiles = attachmentSnapshot.resources.map((file) =>
                file.status === "failed"
                  ? {
                      ...file,
                      onRetry: () =>
                        attachmentResources.retryAttachmentResources(
                          message.messageId,
                        ),
                    }
                  : file,
              );

              return (
                <div
                  className={
                    message.realTimeBooking || isSystemMessage(message)
                      ? s.systemMessageBlock
                      : messageUser?._id === userData?._id
                        ? s.myMessageBlock
                        : s.messageBlock
                  }
                >
                  {!message.realTimeBooking &&
                  !isSystemMessage(message) &&
                  messageUser?._id !== userData?._id &&
                  item.isLastInSenderGroup ? (
                    <div
                      className={`${selectedChat.members.length === 1 ? s.avatarBlock : ""}`}
                    >
                      <CircleAvatar
                        width={"40px"}
                        height={"40px"}
                        fontSize={"12px"}
                        userId={messageUser?._id}
                      />
                    </div>
                  ) : messageUser?._id !== userData?._id &&
                    !message.realTimeBooking &&
                    !isSystemMessage(message) ? (
                    <div className={s.emptyAvatar}></div>
                  ) : null}
                  <SharedTextChatMessage
                    message={{
                      ...message,
                      userId: messageUser?._id ?? "System",
                      type: message.voice
                        ? "voice"
                        : message.realTimeBooking
                          ? "booking"
                          : isSystemMessage(message)
                            ? "system"
                            : "text",
                      voice: message.voice,
                      onVoicePlaybackError: () =>
                        controller.actions.refreshVoicePlaybackUrl(message),
                      attachments: message.files.map((file, index) => ({
                        id:
                          typeof file === "string"
                            ? file
                            : file?._id ||
                              `${message.messageId}-attachment-${index}`,
                        name:
                          typeof file === "string"
                            ? file
                            : file?.filename ||
                              file?.name ||
                              `attachment-${index + 1}`,
                        type:
                          typeof file === "string"
                            ? "file"
                            : file?.type || "file",
                        size:
                          typeof file === "string"
                            ? 0
                            : Number(file?.size || 0),
                        original: typeof file === "string" ? undefined : file,
                      })),
                    }}
                    reactOnMessage={
                      message.isLocalOnly
                        ? undefined
                        : controller.actions.reactOnMessage
                    }
                    actions={messageActions}
                    bodyRenderers={bookingResources.renderers}
                    resolvedFiles={resolvedFiles}
                    focusTargetId={
                      controller.focusTargetMessageId === message.messageId
                        ? getMessageFocusId(message.messageId)
                        : undefined
                    }
                    highlighted={
                      controller.highlightedMessageId === message.messageId
                    }
                    onNavigateToMessage={controller.actions.openMessageById}
                    groupChatMembers={
                      (selectedChat.members || []).length > 1
                        ? selectedChat.members
                        : undefined
                    }
                    name={getTranslation(
                      messageUser?.name,
                      userData?.selectedLanguage,
                    )}
                    surname={
                      messageUser?.surname &&
                      getTranslation(
                        messageUser.surname,
                        userData?.selectedLanguage,
                      )
                    }
                    showName={
                      !message.realTimeBooking &&
                      !isSystemMessage(message) &&
                      message.sender === "other" &&
                      selectedChat.members.length > 1 &&
                      item.isFirstInSenderGroup
                    }
                    style={{
                      myMessageBg: {
                        backgroundColor: "var(--my-message-bg)",
                      },
                    }}
                  />
                </div>
              );
            }}
          />

          <div className={s.chatInputBlock}>
            <div className={s.composerColumn}>
              {controller.replyTarget && (
                <div className={s.replyComposerPreview}>
                  <div className={s.replyComposerTextBlock}>
                    <span className={s.replyComposerLabel}>
                      {strings.globalChatReplyingTo}
                    </span>
                    <span className={s.replyComposerText}>
                      {getReplyPreviewSummary(
                        createReplyPreviewFromMessage(controller.replyTarget),
                      )}
                    </span>
                  </div>
                  <button
                    type="button"
                    className={s.clearReplyButton}
                    aria-label={strings.globalChatClearReply}
                    onClick={controller.actions.clearReplyTarget}
                  >
                    <CloseIcon fontSize="small" />
                  </button>
                </div>
              )}
              {controller.editingMessage && (
                <div className={s.replyComposerPreview}>
                  <div className={s.replyComposerTextBlock}>
                    <span className={s.replyComposerLabel}>
                      {strings.globalChatEditingMessage}
                    </span>
                    <span className={s.replyComposerText}>
                      {controller.editingMessage.text ||
                        strings.globalChatMessagePreview}
                    </span>
                  </div>
                  <button
                    type="button"
                    className={s.clearReplyButton}
                    aria-label={strings.globalChatCancelEditingMessage}
                    onClick={controller.actions.cancelEditingMessage}
                  >
                    <CloseIcon fontSize="small" />
                  </button>
                </div>
              )}
              {controller.selectedTypingIndicatorText && (
                <div
                  className={s.typingIndicator}
                  role="status"
                  aria-live="polite"
                >
                  {controller.selectedTypingIndicatorText}
                </div>
              )}
              <TextChatComposer
                text={controller.composerText}
                selectedFiles={controller.selectedFiles}
                capabilities={{
                  ...globalTextChatContract.capabilities,
                  attachments: controller.editingMessage
                    ? false
                    : globalTextChatContract.capabilities.attachments,
                }}
                attachmentPolicy={globalTextChatContract.attachmentPolicy}
                availability={chatAvailability}
                onTextChange={controller.actions.setComposerText}
                onSelectedFilesChange={controller.actions.setSelectedFiles}
                onSend={controller.actions.sendMessage}
                onFocus={controller.actions.handleComposerFocus}
                onBlur={controller.actions.handleComposerBlur}
                placeholder={strings.typeMessagePlaceholder}
                maxTextAreaHeight={300}
                mentionCandidates={controller.mentionCandidates}
                selectedMentionIds={controller.selectedMentionIds}
                onMentionSelect={controller.actions.addMention}
                hasSendableContent={controller.canSendComposerMessage}
                isCompositionLocked={isVoiceDraftActive}
                contextualAction={
                  globalTextChatContract.capabilities.voiceMessages &&
                  !controller.editingMessage ? (
                    <TextChatVoiceControl
                      availability={chatAvailability}
                      onSend={controller.actions.sendVoiceMessage}
                      onActivityChange={setIsVoiceDraftActive}
                    />
                  ) : undefined
                }
                labels={{
                  input: strings.typeMessagePlaceholder,
                  send: strings.globalChatSendMessage,
                  attach: strings.globalChatAttachFile,
                  fileInput: strings.globalChatAttachFileInput,
                  emoji: strings.globalChatToggleEmojiPicker,
                  mentionSuggestions: strings.globalChatMentionSuggestions,
                  selectedFiles: strings.globalChatSelectedFiles,
                  removeFile: (fileName) =>
                    strings.formatString("globalChatRemoveFile", {
                      name: fileName,
                    }) as string,
                  tooManyFiles: (maxFiles) =>
                    strings.formatString("globalChatTooManyFiles", {
                      count: maxFiles,
                    }) as string,
                  fileTooLarge: (maxFileSizeBytes) =>
                    strings.formatString(strings.fileSizeMoreThan, {
                      size: Math.round(maxFileSizeBytes / 1024 / 1024),
                    }) as string,
                  unsupportedType: strings.globalChatUnsupportedFileType,
                  audioUnsupported:
                    strings.globalChatAudioAttachmentUseVoiceControls,
                  unavailable: strings.chatBlocked,
                }}
              />
            </div>
          </div>
        </div>
      ) : (
        <div className={s.messsagesBlockNotSelect}>
          <SelectChatIcon />
          <span>{strings.selectChat}</span>
        </div>
      )}
      <ConfirmationModal
        modalVisible={Boolean(controller.pendingDeleteMessage)}
        setModalVisible={setDeleteModalVisible}
        confirmTitle={strings.globalChatDeleteMessageTitle}
        description={strings.globalChatDeleteMessageDescription}
        confirmFunc={controller.actions.confirmDeleteMessage}
        loader={controller.isDeletingMessage}
        cancelString={strings.cancelBtn}
        confirmString={strings.calendarModalDeleteBtn}
      />
      <ConfirmationModal
        modalVisible={isUnpinAllModalOpen}
        setModalVisible={setUnpinAllModalVisible}
        confirmTitle={strings.globalChatUnpinAllPinnedMessagesTitle}
        description={strings.globalChatUnpinAllPinnedMessagesDescription}
        confirmFunc={handleUnpinAllPinnedMessages}
        loader={isUnpinningAllPinnedMessages}
        cancelString={strings.cancelBtn}
        confirmString={strings.globalChatUnpinAllPinnedMessages}
      />
      <ModalWindow
        isOpen={massMailModal}
        setIsOpen={setMassMailModal}
        width="fit-content"
      >
        <MassMailing
          setModal={setMassMailModal}
          onSent={() => {
            controller.actions.selectChat(null);
            void controller.actions.loadConversations(0);
          }}
        />
      </ModalWindow>
    </div>
  );
};

export default GlobalChat;
